export type Evidence = {
  label: string;
  title: string;
  excerpt: string;
};

export type AssistantSuggestion = {
  id: string;
  title: string;
  summary: string;
  reason: string;
  recommended?: boolean;
  evidence: Evidence[];
};

export type GenerateSuggestionsInput = {
  text: string;
  accessToken?: string;
  userId?: string;
};

export type GenerateSuggestionsResult = {
  title: string;
  understood: string;
  suggestions: AssistantSuggestion[];
  source: "worker" | "mock" | "local-preview";
};

export interface AIProvider {
  generateSuggestions(input: GenerateSuggestionsInput): Promise<GenerateSuggestionsResult>;
}

export function createFallbackSuggestions(text: string, source: GenerateSuggestionsResult["source"] = "local-preview") {
  const normalized = text.trim() || "地域課題について";
  const isTransport = /交通|バス|移動|免許|デマンド|高齢者|高校生/.test(normalized);
  const center = isTransport ? "公共交通" : "地域課題";

  return {
    title: `${center}の論点整理`,
    understood: `「${normalized.slice(0, 58)}${normalized.length > 58 ? "..." : ""}」という問題意識として受け取りました。`,
    source,
    suggestions: [
      {
        id: "progress",
        title: "過去答弁の進捗を問う",
        summary: "過去に市が検討するとした内容について、その後の実施状況を確認します。",
        reason: "同じ必要性を改めて問うより、答弁後の変化を確認する方が質問の新規性が出ます。",
        recommended: true,
        evidence: [
          {
            label: "自自治体議会",
            title: "令和5年6月定例会 一般質問",
            excerpt: "高齢者の移動支援について、導入可能性を検討する旨の答弁があります。",
          },
          {
            label: "行政資料",
            title: "地域公共交通計画",
            excerpt: "移動需要の変化を踏まえた交通ネットワークの見直しが示されています。",
          },
        ],
      },
      {
        id: "unexplored",
        title: "未議論の対象に焦点を変える",
        summary: "高齢者だけでなく、高校生や子育て世帯の移動に焦点を当てます。",
        reason: "既存の議論と重なりにくく、生活実感から政策提案へつなげやすい切り口です。",
        evidence: [
          {
            label: "議会での傾向",
            title: "関連質問の分類",
            excerpt: "高齢者移動の言及が多い一方、通学・若年層の移動は少ない傾向です。",
          },
        ],
      },
      {
        id: "plan-gap",
        title: "行政計画と実態の差を問う",
        summary: "計画上の目標と、住民が感じている移動不便の差を確認します。",
        reason: "行政資料を根拠にしながら、現場の声を質問に落とし込めます。",
        evidence: [
          {
            label: "行政資料",
            title: "総合計画・個別計画",
            excerpt: "地域間格差の是正、暮らしやすさの維持に関する記載が参照候補です。",
          },
        ],
      },
    ],
  };
}
